import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  FORM_TITLES,
  formatDateLabel,
  formatMonthLabel,
  formatRoleLabel,
  getMetricEntryValue,
  normalizeFieldReport,
  getWorkOverdueInfo,
  NETPLUS_STATUS_FIELDS,
  NETPLUS_MGR_FIELDS,
  withNetplusMgrRunningTotals,
  istDayKey,
  normalizeRemarksHistory,
} from "./reportingConfig";

const BRAND_DARK = "FF1F2937"; // slate-800
const BRAND_ACCENT = "FFF59E0B"; // amber-500
const HEADER_TEXT = "FFFFFFFF";
const ROW_ALT = "FFF9FAFB"; // gray-50
const BORDER_COLOR = "FFD1D5DB"; // gray-300
const LINK_COLOR = "FF2563EB"; // blue-600

const thinBorder = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

const sanitizeFileName = (value) =>
  String(value || "report")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

// Excel worksheet names: max 31 chars, no \ / ? * [ ] : , must be unique in
// the workbook. Used whenever several reports (each with its own work title)
// are combined into one downloaded file so tabs never collide or get rejected.
const MAX_SHEET_NAME_LENGTH = 31;
const sanitizeSheetName = (value) =>
  String(value || "Sheet")
    .replace(/[\\/?*[\]:]/g, "-")
    .trim()
    .slice(0, MAX_SHEET_NAME_LENGTH) || "Sheet";

const uniqueSheetName = (baseName, usedNames) => {
  const base = sanitizeSheetName(baseName);
  let candidate = base;
  let suffix = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    const suffixText = ` (${suffix})`;
    candidate =
      base.slice(0, MAX_SHEET_NAME_LENGTH - suffixText.length) + suffixText;
    suffix += 1;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
};

// Tab-friendly label for a report — the sheet's own "work" title when it has
// one (Form 1), otherwise the form name plus its period, so a workbook with
// several reports reads as "GATE WORK", "PARKING WORK", ... rather than a
// stack of generically named sheets.
const getReportLabel = (report, formData, index) => {
  const formType = Number(report.formType);
  if (formType === 1) return formData.work || `Work Sheet ${index + 1}`;
  if (formType === 2 || formType === 3 || formType === 4) {
    return `${FORM_TITLES[formType]} - ${formData.month || report.periodKey || index + 1}`;
  }
  if (formType === 5 || formType === 6) {
    return `${FORM_TITLES[formType]} - ${report.periodKey || formatDateLabel(report.reportDate)}`;
  }
  return FORM_TITLES[formType] || `Sheet ${index + 1}`;
};

const setColumnWidths = (worksheet, widths) => {
  widths.forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });
};

// Merged title banner across the given column span, dark background + amber
// accent underline so every sheet reads as one branded report, not a raw dump.
const writeBanner = (worksheet, rowIndex, colSpan, title, subtitle) => {
  const titleRow = worksheet.getRow(rowIndex);
  worksheet.mergeCells(rowIndex, 1, rowIndex, colSpan);
  titleRow.getCell(1).value = title;
  titleRow.getCell(1).font = { bold: true, size: 16, color: { argb: HEADER_TEXT } };
  titleRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };
  titleRow.height = 28;
  for (let c = 1; c <= colSpan; c += 1) {
    titleRow.getCell(c).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: BRAND_DARK },
    };
  }

  if (subtitle) {
    const subtitleRow = worksheet.getRow(rowIndex + 1);
    worksheet.mergeCells(rowIndex + 1, 1, rowIndex + 1, colSpan);
    subtitleRow.getCell(1).value = subtitle;
    subtitleRow.getCell(1).font = { italic: true, size: 10, color: { argb: HEADER_TEXT } };
    subtitleRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };
    subtitleRow.height = 18;
    for (let c = 1; c <= colSpan; c += 1) {
      subtitleRow.getCell(c).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: BRAND_DARK },
      };
    }
    return rowIndex + 2;
  }

  return rowIndex + 1;
};

// Label/value info grid (Employee, Role, Status, Period, Completion, Generated On)
const writeInfoGrid = (worksheet, rowIndex, colSpan, pairs) => {
  let row = rowIndex;
  for (let i = 0; i < pairs.length; i += 2) {
    const left = pairs[i];
    const right = pairs[i + 1];
    const excelRow = worksheet.getRow(row);

    excelRow.getCell(1).value = left.label;
    excelRow.getCell(1).font = { bold: true, size: 10, color: { argb: "FF6B7280" } };
    excelRow.getCell(2).value = left.value ?? "-";
    excelRow.getCell(2).font = { size: 11 };

    if (right) {
      const midCol = Math.ceil(colSpan / 2) + 1;
      excelRow.getCell(midCol).value = right.label;
      excelRow.getCell(midCol).font = { bold: true, size: 10, color: { argb: "FF6B7280" } };
      excelRow.getCell(midCol + 1).value = right.value ?? "-";
      excelRow.getCell(midCol + 1).font = { size: 11 };
    }

    row += 1;
  }
  return row + 1;
};

// Generic bordered/striped table writer shared by every form's breakdown —
// keeps every section of every sheet visually consistent.
const writeTable = (worksheet, rowIndex, colSpan, sectionTitle, columns, rows) => {
  let row = rowIndex;

  if (sectionTitle) {
    const titleRow = worksheet.getRow(row);
    worksheet.mergeCells(row, 1, row, colSpan);
    titleRow.getCell(1).value = sectionTitle;
    titleRow.getCell(1).font = { bold: true, size: 12, color: { argb: "FF111827" } };
    titleRow.height = 20;
    row += 1;
  }

  const headerRow = worksheet.getRow(row);
  columns.forEach((col, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = col.label;
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_ACCENT } };
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    cell.border = thinBorder;
  });
  headerRow.height = 20;
  row += 1;

  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) {
    const emptyRow = worksheet.getRow(row);
    worksheet.mergeCells(row, 1, row, colSpan);
    emptyRow.getCell(1).value = "No entries recorded yet.";
    emptyRow.getCell(1).font = { italic: true, color: { argb: "FF9CA3AF" }, size: 10 };
    emptyRow.getCell(1).alignment = { horizontal: "center" };
    emptyRow.getCell(1).border = thinBorder;
    row += 1;
    return row + 1;
  }

  list.forEach((entry, entryIndex) => {
    const dataRow = worksheet.getRow(row);
    columns.forEach((col, colIndex) => {
      const cell = dataRow.getCell(colIndex + 1);
      cell.value = col.render ? col.render(entry) : entry[col.key] ?? "-";
      cell.font = { size: 10 };
      cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      cell.border = thinBorder;
      if (entryIndex % 2 === 1) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ROW_ALT } };
      }
    });
    row += 1;
  });

  return row + 1;
};

// Builds the sheet(s) for one report inside the given workbook. Returns the
// list of worksheet names created, in display order, so callers can link to
// the first one from an index/summary table.
const addReportSheets = (workbook, report, technicalStaffById, usedNames, label) => {
  const formType = Number(report.formType);
  const formData = report.formData || {};

  if (formType === 1) {
    // Main works → sub-works with their deadline and any extended date.
    const field = normalizeFieldReport(formData);
    const sheetNames = [];
    if (field.mainWorks.length) {
      const worksName = uniqueSheetName(`${label} - Works`, usedNames);
      const wsWorks = workbook.addWorksheet(worksName);
      const workColumns = [
        { key: "main", label: "Main work", width: 26 },
        { key: "sub", label: "Sub-work", width: 26 },
        { key: "status", label: "Status", width: 14 },
        { key: "start", label: "Start Date", width: 14 },
        { key: "deadline", label: "Deadline", width: 14 },
        { key: "extended", label: "Extended Date", width: 16 },
        { key: "extensions", label: "Times Extended", width: 14 },
        { key: "completedOn", label: "Completed On", width: 14 },
      ];
      setColumnWidths(wsWorks, workColumns.map((c) => c.width));
      const rowOf = (work, mainName, subName) => {
        const info = getWorkOverdueInfo(work);
        const exts = Array.isArray(work.extensions) ? work.extensions.length : 0;
        return {
          main: mainName,
          sub: subName,
          status: work.status || "Pending",
          start: formatDateLabel(work.startDate),
          deadline: formatDateLabel(work.completionDate),
          extended: info?.extended ? formatDateLabel(info.extendedDate) : "-",
          extensions: info?.extended ? exts || 1 : 0,
          completedOn: work.completedOn ? formatDateLabel(work.completedOn) : "-",
        };
      };
      const workRows = [];
      field.mainWorks.forEach((main) => {
        const mainName = main.mainWorkName || "Untitled work";
        workRows.push(rowOf(main, mainName, "—"));
        (main.subWorks || []).forEach((sub) => {
          workRows.push(rowOf(sub, mainName, sub.subWorkName || "Untitled sub-work"));
        });
      });
      const wRow = writeBanner(wsWorks, 1, workColumns.length, label, FORM_TITLES[1]);
      writeTable(wsWorks, wRow, workColumns.length, "Works & Deadlines", workColumns, workRows);
      sheetNames.push(worksName);
    }

    const subWorksName = uniqueSheetName(`${label} - Sub Works`, usedNames);
    const worksheet = workbook.addWorksheet(subWorksName);
    const columns = [
      { key: "subWorkName", label: "Sub-work", width: 26 },
      {
        key: "workStartDate",
        label: "Start Date",
        width: 16,
        render: (r) => formatDateLabel(r.workStartDate),
      },
      {
        key: "workExtendDate",
        label: "Extend Date",
        width: 16,
        render: (r) => formatDateLabel(r.workExtendDate),
      },
      { key: "status", label: "Status", width: 16 },
    ];
    setColumnWidths(worksheet, [26, 16, 16, 16]);

    let row = writeBanner(worksheet, 1, 4, label, FORM_TITLES[1]);
    row = writeInfoGrid(worksheet, row, 4, [
      { label: "Work Assign Date", value: formatDateLabel(formData.workAssignDate) },
      { label: "Status", value: report.status || "Pending" },
      { label: "Completion", value: `${report.completionPercent ?? 0}%` },
      {
        label: "Last Edited",
        value: formatDateLabel(report.lastEditedAt || report.updatedAt),
      },
    ]);

    writeTable(
      worksheet,
      row,
      4,
      "Sub Works",
      columns,
      Array.isArray(formData.subWorks) ? formData.subWorks : [],
    );

    const historyName = uniqueSheetName(`${label} - History`, usedNames);
    const historySheet = workbook.addWorksheet(historyName);
    setColumnWidths(historySheet, [22, 16, 40, 18]);
    const historyColumns = [
      { key: "work", label: "Work / Sub-work", width: 22 },
      {
        key: "date",
        label: "Date",
        width: 16,
        render: (r) => formatDateLabel(r.date),
      },
      { key: "remarks", label: "Remarks", width: 40, render: (r) => r.remarks || "-" },
      { key: "status", label: "Status", width: 18 },
    ];
    const hRow = writeBanner(historySheet, 1, 4, "Status History", label);
    writeTable(
      historySheet,
      hRow,
      4,
      null,
      historyColumns,
      (Array.isArray(formData.subStatusHistory) ? formData.subStatusHistory : []).map(
        (entry) => ({ ...entry, work: entry.work || formData.work || "General" }),
      ),
    );

    return [...sheetNames, subWorksName, historyName];
  }

  if (formType === 2 || formType === 3 || formType === 4) {
    const sheetName = uniqueSheetName(label, usedNames);
    const worksheet = workbook.addWorksheet(sheetName);
    const metrics = Array.isArray(formData.metrics) ? formData.metrics : [];
    const dateSet = new Set();
    metrics.forEach((metric) =>
      (Array.isArray(metric?.entries) ? metric.entries : []).forEach((entry) => {
        if (entry?.date) dateSet.add(entry.date);
      }),
    );
    const dates = Array.from(dateSet).sort();

    const columns = [
      { key: "label", label: "Metric", width: 26 },
      ...dates.map((date) => ({
        key: date,
        label: formatDateLabel(date),
        width: 14,
        render: (metric) => getMetricEntryValue(metric, date) || "-",
      })),
    ];
    // Real Estate Sales (formType 4) adds a Customer / Dealer Log table below
    // the metric grid — 6 columns wide, so the sheet's banner/info grid span
    // has to account for it too.
    const colSpan = Math.max(columns.length, formType === 4 ? 6 : 4);
    setColumnWidths(
      worksheet,
      columns.map((c) => c.width),
    );

    let row = writeBanner(worksheet, 1, colSpan, label, FORM_TITLES[formType]);
    row = writeInfoGrid(worksheet, row, colSpan, [
      { label: "Status", value: report.status || "Pending" },
      { label: "Completion", value: `${report.completionPercent ?? 0}%` },
      {
        label: "Last Edited",
        value: formatDateLabel(report.lastEditedAt || report.updatedAt),
      },
    ]);

    if (metrics.length) {
      row = writeTable(
        worksheet,
        row,
        colSpan,
        "Metric Entries",
        columns.map((c) => ({ key: c.key, label: c.label, render: c.render })),
        metrics,
      );
    }

    // Tele Calling (3) & Real Estate Sales (4) — contact list, flattened call
    // log, and (Sales) the manual daily Visits/Sales rows.
    if (formType === 3 || formType === 4) {
      const contacts = Array.isArray(formData.contacts)
        ? formData.contacts
        : [];
      // Remarks/call history lives on the Master Customer record
      // (dailyRemarks, managed from the Customer Database page) — merge in
      // any older per-report `calls` entries too, de-duped.
      const contactHistory = (c) => {
        const history = Array.isArray(c.dailyRemarks) ? c.dailyRemarks : [];
        const calls = Array.isArray(c.calls) ? c.calls : [];
        const seen = new Set(history.map((h) => `${h.date}::${h.remarks}`));
        return [
          ...history.map((h) => ({ date: h.date, remarks: h.remarks, by: h.by })),
          ...calls
            .filter(
              (call) => !seen.has(`${call.date}::${(call.remarks || "").trim()}`),
            )
            .map((call) => ({ date: call.date, remarks: call.remarks })),
        ];
      };
      [14, 22, 18, 26, 16, 16, 14, 8].forEach((width, index) => {
        const column = worksheet.getColumn(index + 1);
        if (!column.width || column.width < width) column.width = width;
      });
      row = writeTable(
        worksheet,
        row,
        colSpan,
        "Contacts",
        [
          {
            key: "createdDate",
            label: "Added On",
            render: (r) => (r.createdDate ? formatDateLabel(r.createdDate) : "-"),
          },
          { key: "customerName", label: "Customer" },
          { key: "customerPhone", label: "Customer Phone" },
          { key: "remarks", label: "Remarks" },
          { key: "lead", label: "Lead" },
          { key: "propertyType", label: "Type" },
          { key: "budget", label: "Budget" },
          {
            key: "calls",
            label: "Calls",
            render: (r) => contactHistory(r).length,
          },
        ],
        contacts,
      );
      const callLog = contacts
        .flatMap((c) =>
          contactHistory(c).map((entry) => ({
            date: entry.date,
            customer: c.customerName || c.customerPhone || "-",
            by: entry.by || "-",
            remarks: entry.remarks || "-",
          })),
        )
        .sort((a, b) => String(b.date).localeCompare(String(a.date)));
      row = writeTable(
        worksheet,
        row,
        colSpan,
        "Call Log",
        [
          {
            key: "date",
            label: "Date",
            render: (r) => (r.date ? formatDateLabel(r.date) : "-"),
          },
          { key: "by", label: "By" },
          { key: "customer", label: "Customer" },
          { key: "remarks", label: "Remark" },
        ],
        callLog,
      );
      if (formType === 4) {
        row = writeTable(
          worksheet,
          row,
          colSpan,
          "Daily Activity",
          [
            {
              key: "date",
              label: "Date",
              render: (r) => (r.date ? formatDateLabel(r.date) : "-"),
            },
            {
              key: "openVisits",
              label: "Open Visits",
              render: (r) => r.openVisits ?? r.visits ?? 0,
            },
            { key: "siteVisits", label: "Site Visits" },
            { key: "calls", label: "Phone Calls" },
            { key: "sales", label: "Sales (deals)" },
            { key: "remarks", label: "Remarks" },
          ],
          Array.isArray(formData.dailyStats) ? formData.dailyStats : [],
        );
      }
    }

    if (
      formType === 4 &&
      Array.isArray(formData.customerDealerLog) &&
      formData.customerDealerLog.length > 0
    ) {
      // Legacy Customer / Dealer Log — only for sheets that still carry it.
      // Widen columns A–E so the log stays readable even when few/no metric
      // date columns exist to stretch the sheet.
      [22, 24, 18, 24, 18].forEach((width, index) => {
        const column = worksheet.getColumn(index + 1);
        if (!column.width || column.width < width) column.width = width;
      });
      writeTable(
        worksheet,
        row,
        colSpan,
        "Customer / Dealer Log",
        [
          {
            key: "date",
            label: "Date",
            render: (r) => (r.date ? formatDateLabel(r.date) : "-"),
          },
          { key: "customerName", label: "Customer Name" },
          { key: "customerPhone", label: "Customer Phone" },
          { key: "dealerName", label: "Dealer Name" },
          { key: "dealerPhone", label: "Dealer Phone" },
        ],
        Array.isArray(formData.customerDealerLog)
          ? formData.customerDealerLog
          : [],
      );
    }

    return [sheetName];
  }

  if (formType === 8) {
    const sheetName = uniqueSheetName(label, usedNames);
    const worksheet = workbook.addWorksheet(sheetName);
    const entries = withNetplusMgrRunningTotals(formData.entries);
    // Cumulative figures read "day (total till date)" in one column; a
    // back-dated day shows the day it was really submitted.
    const rows = entries.map((e) => {
      const savedDay = e.savedAt ? istDayKey(e.savedAt) : "";
      const r = {
        date: e.date,
        submittedOn: savedDay > e.date ? formatDateLabel(savedDay) : "",
      };
      NETPLUS_MGR_FIELDS.forEach(({ key, cumulative }) => {
        const day = e[key] === "" || e[key] == null ? "" : e[key];
        r[key] = cumulative
          ? `${day === "" ? 0 : day} (${e.running[key]})`
          : day === ""
            ? "-"
            : day;
      });
      return r;
    });
    const columns = [
      { key: "date", label: "Date", render: (r) => formatDateLabel(r.date) },
      ...NETPLUS_MGR_FIELDS.map(({ key, label: fieldLabel, cumulative }) => ({
        key,
        label: cumulative ? `${fieldLabel} (total till date)` : fieldLabel,
      })),
      { key: "submittedOn", label: "Submitted On (if late)" },
    ];
    const colSpan = Math.max(columns.length, 4);
    let row = writeBanner(worksheet, 1, colSpan, label, FORM_TITLES[formType]);
    row = writeInfoGrid(worksheet, row, colSpan, [
      { label: "Status", value: report.status || "Pending" },
      { label: "Completion", value: `${report.completionPercent ?? 0}%` },
      {
        label: "Last Edited",
        value: formatDateLabel(report.lastEditedAt || report.updatedAt),
      },
    ]);
    writeTable(worksheet, row, colSpan, "Daily Figures", columns, rows);
    return [sheetName];
  }

  if (formType === 7) {
    const sheetName = uniqueSheetName(label, usedNames);
    const worksheet = workbook.addWorksheet(sheetName);
    const areas = Array.isArray(formData.areas) ? formData.areas : [];
    const dateSet = new Set();
    areas.forEach((area) =>
      (Array.isArray(area?.entries) ? area.entries : []).forEach((entry) => {
        if (entry?.date) dateSet.add(entry.date);
      }),
    );
    const dates = Array.from(dateSet).sort();
    // One row per area × status, columns are the dates that have entries.
    const rows = areas.flatMap((area) =>
      NETPLUS_STATUS_FIELDS.map(({ key, label: statusLabel }) => ({
        rowLabel: `Area ${area.code} — ${statusLabel}`,
        ...Object.fromEntries(
          dates.map((date) => [
            date,
            (Array.isArray(area.entries) ? area.entries : []).find(
              (entry) => entry.date === date,
            )?.[key] ?? "",
          ]),
        ),
      })),
    );
    const columns = [
      { key: "rowLabel", label: "Area / Status" },
      ...dates.map((date) => ({
        key: date,
        label: formatDateLabel(date),
        render: (r) => (r[date] === "" || r[date] == null ? "-" : r[date]),
      })),
    ];
    const colSpan = Math.max(columns.length, 4);
    let row = writeBanner(worksheet, 1, colSpan, label, FORM_TITLES[formType]);
    row = writeInfoGrid(worksheet, row, colSpan, [
      { label: "Status", value: report.status || "Pending" },
      { label: "Completion", value: `${report.completionPercent ?? 0}%` },
      {
        label: "Last Edited",
        value: formatDateLabel(report.lastEditedAt || report.updatedAt),
      },
    ]);
    writeTable(worksheet, row, colSpan, "Area Counts", columns, rows);
    return [sheetName];
  }

  if (formType === 5 || formType === 6) {
    const sheetName = uniqueSheetName(label, usedNames);
    const worksheet = workbook.addWorksheet(sheetName);

    const columns =
      formType === 5
        ? [
            {
              key: "assignDate",
              label: "Assign Date",
              width: 16,
              render: (r) => formatDateLabel(r.assignDate),
            },
            { key: "work", label: "Work", width: 30 },
            {
              key: "dueDate",
              label: "Due Date",
              width: 16,
              render: (r) => formatDateLabel(r.dueDate),
            },
            { key: "status", label: "Status", width: 16 },
            { key: "remarks", label: "Remarks", width: 30, render: (r) => r.remarks || "-" },
          ]
        : [
            {
              key: "workAssignDate",
              label: "Work Assign Date",
              width: 16,
              render: (r) => formatDateLabel(r.workAssignDate),
            },
            { key: "work", label: "Work", width: 28 },
            {
              key: "workDueDate",
              label: "Due Date",
              width: 16,
              render: (r) => formatDateLabel(r.workDueDate),
            },
            { key: "status", label: "Status", width: 16 },
            {
              key: "assignedTechnicalStaff",
              label: "Technical Staff",
              width: 22,
              render: (r) => {
                const person = technicalStaffById.get(r.assignedTechnicalStaff);
                return person ? `${person.name} - ${formatRoleLabel(person.role)}` : "-";
              },
            },
            { key: "remarks", label: "Remarks", width: 28, render: (r) => r.remarks || "-" },
            {
              key: "updatedRemarks",
              label: "Updated Remarks",
              width: 40,
              render: (r) => {
                const history = normalizeRemarksHistory(r.updatedRemarks);
                return history.length
                  ? history
                      .map(
                        (entry) =>
                          `${formatDateLabel(entry.date)}: ${entry.text}`,
                      )
                      .join(" | ")
                  : "-";
              },
            },
          ];

    setColumnWidths(
      worksheet,
      columns.map((c) => c.width),
    );

    let row = writeBanner(worksheet, 1, columns.length, label, FORM_TITLES[formType]);
    row = writeInfoGrid(worksheet, row, columns.length, [
      { label: "Status", value: report.status || "Pending" },
      { label: "Completion", value: `${report.completionPercent ?? 0}%` },
      {
        label: "Last Edited",
        value: formatDateLabel(report.lastEditedAt || report.updatedAt),
      },
    ]);

    writeTable(
      worksheet,
      row,
      columns.length,
      "Tasks",
      columns,
      Array.isArray(formData.tasks) ? formData.tasks : [],
    );

    return [sheetName];
  }

  return [];
};

// Single-report download — used on the report Detail page.
export const exportStaffReportToExcel = async ({
  report,
  employeeName,
  employeeRole,
  technicalStaffById = new Map(),
}) => {
  if (!report) return;

  const formType = Number(report.formType);
  const formData = report.formData || {};
  const usedNames = new Set();

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Sattiandco Staff Reporting";
  workbook.created = new Date();

  const summaryName = uniqueSheetName("Summary", usedNames);
  const summarySheet = workbook.addWorksheet(summaryName);
  setColumnWidths(summarySheet, [22, 30, 22, 30]);
  let summaryRow = writeBanner(
    summarySheet,
    1,
    4,
    "Staff Reporting Sheet",
    FORM_TITLES[formType] || "Reporting Sheet",
  );
  writeInfoGrid(summarySheet, summaryRow, 4, [
    { label: "Employee Name", value: employeeName },
    { label: "Role", value: formatRoleLabel(employeeRole) },
    { label: "Status", value: report.status || "Pending" },
    { label: "Completion", value: `${report.completionPercent ?? 0}%` },
    { label: "Report Date", value: formatDateLabel(report.reportDate) },
    { label: "Period", value: report.periodKey || "-" },
    {
      label: "Last Edited",
      value: formatDateLabel(report.lastEditedAt || report.updatedAt),
    },
    { label: "Generated On", value: formatDateLabel(new Date()) },
  ]);

  const label = getReportLabel(report, formData, 0);
  addReportSheets(workbook, report, technicalStaffById, usedNames, label);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const fileName = `${sanitizeFileName(employeeName)}-${sanitizeFileName(
    FORM_TITLES[formType] || "report",
  )}-${sanitizeFileName(report.periodKey || formatDateLabel(report.reportDate))}.xlsx`;

  saveAs(blob, fileName);
};

// Whole-employee download — used on the Employee Reports page. Every report
// (work sheet) the employee has becomes its own tab(s) in one workbook, named
// after its work title (e.g. "GATE WORK - Sub Works", "PARKING WORK - Sub
// Works"), with an index sheet up front linking to each one.
export const exportEmployeeReportsToExcel = async ({
  employee,
  reports,
  technicalStaffById = new Map(),
}) => {
  const list = Array.isArray(reports) ? reports : [];
  const employeeName = employee?.name || "Staff member";
  const employeeRole = employee?.role || "";

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Sattiandco Staff Reporting";
  workbook.created = new Date();

  const usedNames = new Set();
  const summaryName = uniqueSheetName("Summary", usedNames);
  const summarySheet = workbook.addWorksheet(summaryName);
  const summaryColSpan = 7;
  setColumnWidths(summarySheet, [6, 32, 22, 16, 14, 12, 18]);

  let row = writeBanner(
    summarySheet,
    1,
    summaryColSpan,
    "Employee Work Sheets",
    `${employeeName} - ${formatRoleLabel(employeeRole)}`,
  );
  row = writeInfoGrid(summarySheet, row, summaryColSpan, [
    { label: "Employee", value: employeeName },
    { label: "Role", value: formatRoleLabel(employeeRole) },
    { label: "Total Sheets", value: list.length },
    { label: "Generated On", value: formatDateLabel(new Date()) },
  ]);

  // Build each report's sheets first so we know which tab to link every
  // index row to, then write the index table with working hyperlinks.
  const indexEntries = list.map((report, index) => {
    const formData = report.formData || {};
    const label = getReportLabel(report, formData, index);
    const sheetNames = addReportSheets(
      workbook,
      report,
      technicalStaffById,
      usedNames,
      label,
    );
    return {
      index: index + 1,
      label,
      form: FORM_TITLES[Number(report.formType)] || "-",
      period: report.periodKey || "-",
      status: report.status || (report.isComplete ? "Complete" : "Pending"),
      completion: `${report.completionPercent ?? 0}%`,
      lastEdited: formatDateLabel(report.lastEditedAt || report.updatedAt),
      linkTo: sheetNames[0] || null,
    };
  });

  const indexColumns = [
    { key: "index", label: "#", width: 6 },
    { key: "label", label: "Work / Sheet", width: 32 },
    { key: "form", label: "Form", width: 22 },
    { key: "period", label: "Period", width: 16 },
    { key: "status", label: "Status", width: 14 },
    { key: "completion", label: "Completion", width: 12 },
    { key: "lastEdited", label: "Last Edited", width: 18 },
  ];

  const titleRow = summarySheet.getRow(row);
  summarySheet.mergeCells(row, 1, row, summaryColSpan);
  titleRow.getCell(1).value = "All Work Sheets";
  titleRow.getCell(1).font = { bold: true, size: 12, color: { argb: "FF111827" } };
  titleRow.height = 20;
  row += 1;

  const headerRow = summarySheet.getRow(row);
  indexColumns.forEach((col, colIndex) => {
    const cell = headerRow.getCell(colIndex + 1);
    cell.value = col.label;
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_ACCENT } };
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    cell.border = thinBorder;
  });
  headerRow.height = 20;
  row += 1;

  if (!indexEntries.length) {
    const emptyRow = summarySheet.getRow(row);
    summarySheet.mergeCells(row, 1, row, summaryColSpan);
    emptyRow.getCell(1).value = "No work sheets recorded yet.";
    emptyRow.getCell(1).font = { italic: true, color: { argb: "FF9CA3AF" }, size: 10 };
    emptyRow.getCell(1).alignment = { horizontal: "center" };
    emptyRow.getCell(1).border = thinBorder;
  }

  indexEntries.forEach((entry, entryIndex) => {
    const dataRow = summarySheet.getRow(row);
    indexColumns.forEach((col, colIndex) => {
      const cell = dataRow.getCell(colIndex + 1);
      if (col.key === "label" && entry.linkTo) {
        cell.value = {
          text: entry.label,
          hyperlink: `#'${entry.linkTo}'!A1`,
        };
        cell.font = { size: 10, color: { argb: LINK_COLOR }, underline: true };
      } else {
        cell.value = entry[col.key] ?? "-";
        cell.font = { size: 10 };
      }
      cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      cell.border = thinBorder;
      if (entryIndex % 2 === 1) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ROW_ALT } };
      }
    });
    row += 1;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const fileName = `${sanitizeFileName(employeeName)}-all-worksheets-${sanitizeFileName(
    formatDateLabel(new Date()),
  )}.xlsx`;

  saveAs(blob, fileName);
};

// Team Efforts — one workbook covering every Tele Calling / Real Estate
// Sales employee's Customer Database roster: a summary index sheet (linked
// to each employee's own tab) plus, per employee, their customer roster and
// a flattened call/remarks log — the admin-facing "who's doing what" export.
export const exportTeamEffortsToExcel = async ({ employees, rosterByEmployeeId }) => {
  const list = Array.isArray(employees) ? employees : [];
  const rosterOf = (id) =>
    (rosterByEmployeeId instanceof Map
      ? rosterByEmployeeId.get(String(id))
      : rosterByEmployeeId?.[String(id)]) || [];

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Sattiandco Staff Reporting";
  workbook.created = new Date();

  const usedNames = new Set();
  const summaryName = uniqueSheetName("Summary", usedNames);
  const summarySheet = workbook.addWorksheet(summaryName);
  const summaryColSpan = 7;
  setColumnWidths(summarySheet, [6, 30, 20, 16, 14, 14, 18]);

  let row = writeBanner(
    summarySheet,
    1,
    summaryColSpan,
    "Team Efforts Report",
    "Customer Database — every Tele Calling / Real Estate Sales employee",
  );
  row = writeInfoGrid(summarySheet, row, summaryColSpan, [
    { label: "Employees", value: list.length },
    { label: "Generated On", value: formatDateLabel(new Date()) },
  ]);

  const indexColumns = [
    { key: "index", label: "#", width: 6 },
    { key: "name", label: "Employee", width: 30 },
    { key: "role", label: "Role", width: 20 },
    { key: "active", label: "Active Customers", width: 16 },
    { key: "archived", label: "Archived", width: 14 },
    { key: "remarks", label: "Total Remarks", width: 14 },
    { key: "lastActivity", label: "Last Activity", width: 18 },
  ];

  // Build each employee's sheet first so the index table can link straight
  // to it, same pattern as the whole-employee export above.
  const indexEntries = list.map((employee, index) => {
    const customers = rosterOf(employee._id || employee.id);
    const active = customers.filter((c) => !c.archived).length;
    const archived = customers.length - active;
    const remarkDates = customers.flatMap((c) =>
      (Array.isArray(c.dailyRemarks) ? c.dailyRemarks : []).map((r) => r.date),
    );
    const totalRemarks = remarkDates.length;
    const lastActivity = remarkDates.sort().slice(-1)[0];

    const sheetName = uniqueSheetName(employee.name || `Employee ${index + 1}`, usedNames);
    const sheet = workbook.addWorksheet(sheetName);
    setColumnWidths(sheet, [22, 18, 16, 14, 14, 12]);

    let r = writeBanner(sheet, 1, 6, employee.name || "Employee", formatRoleLabel(employee.role));
    r = writeInfoGrid(sheet, r, 6, [
      { label: "Active Customers", value: active },
      { label: "Archived", value: archived },
      { label: "Total Remarks Logged", value: totalRemarks },
      { label: "Generated On", value: formatDateLabel(new Date()) },
    ]);

    r = writeTable(
      sheet,
      r,
      6,
      "Customer Roster",
      [
        { key: "name", label: "Customer Name" },
        { key: "phone", label: "Customer Phone" },
        { key: "lead", label: "Lead" },
        { key: "propertyType", label: "Type" },
        { key: "budget", label: "Budget" },
        { key: "createdDate", label: "Month", render: (c) => formatMonthLabel(c.createdDate) },
      ],
      customers,
    );

    const callLog = customers
      .flatMap((c) =>
        (Array.isArray(c.dailyRemarks) ? c.dailyRemarks : []).map((h) => ({
          date: h.date,
          customer: c.name || c.phone || "-",
          by: h.by || "-",
          remarks: h.remarks || "-",
        })),
      )
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));

    writeTable(
      sheet,
      r,
      6,
      "Call Log",
      [
        { key: "date", label: "Date", render: (x) => (x.date ? formatDateLabel(x.date) : "-") },
        { key: "customer", label: "Customer" },
        { key: "by", label: "By" },
        { key: "remarks", label: "Remark" },
      ],
      callLog,
    );

    return {
      index: index + 1,
      name: employee.name || "-",
      role: formatRoleLabel(employee.role),
      active,
      archived,
      remarks: totalRemarks,
      lastActivity: lastActivity ? formatDateLabel(lastActivity) : "-",
      linkTo: sheetName,
    };
  });

  const titleRow = summarySheet.getRow(row);
  summarySheet.mergeCells(row, 1, row, summaryColSpan);
  titleRow.getCell(1).value = "All Employees";
  titleRow.getCell(1).font = { bold: true, size: 12, color: { argb: "FF111827" } };
  titleRow.height = 20;
  row += 1;

  const headerRow = summarySheet.getRow(row);
  indexColumns.forEach((col, colIndex) => {
    const cell = headerRow.getCell(colIndex + 1);
    cell.value = col.label;
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_ACCENT } };
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    cell.border = thinBorder;
  });
  headerRow.height = 20;
  row += 1;

  if (!indexEntries.length) {
    const emptyRow = summarySheet.getRow(row);
    summarySheet.mergeCells(row, 1, row, summaryColSpan);
    emptyRow.getCell(1).value = "No employees found.";
    emptyRow.getCell(1).font = { italic: true, color: { argb: "FF9CA3AF" }, size: 10 };
    emptyRow.getCell(1).alignment = { horizontal: "center" };
    emptyRow.getCell(1).border = thinBorder;
  }

  indexEntries.forEach((entry, entryIndex) => {
    const dataRow = summarySheet.getRow(row);
    indexColumns.forEach((col, colIndex) => {
      const cell = dataRow.getCell(colIndex + 1);
      if (col.key === "name" && entry.linkTo) {
        cell.value = { text: entry.name, hyperlink: `#'${entry.linkTo}'!A1` };
        cell.font = { size: 10, color: { argb: LINK_COLOR }, underline: true };
      } else {
        cell.value = entry[col.key] ?? "-";
        cell.font = { size: 10 };
      }
      cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      cell.border = thinBorder;
      if (entryIndex % 2 === 1) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ROW_ALT } };
      }
    });
    row += 1;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const fileName = `team-efforts-${sanitizeFileName(formatDateLabel(new Date()))}.xlsx`;

  saveAs(blob, fileName);
};
